import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import Header from 'shared/layout/Header';
import { useStateContext } from 'shared/contexts/ContextProvider';
import { storeService } from 'features/company/api';
import { loadList } from 'shared/api/requests';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatMoney = (val) => {
  try {
    return Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return val;
  }
};

const ProductStats = () => {
  const today = formatDate(new Date());
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(formatDate(new Date(new Date().setDate(new Date().getDate() - 7))));
  const [endDate, setEndDate] = useState(today);
  const [sortBy, setSortBy] = useState('total_sales_sum'); // total_sales_sum | total_sales_profit | closing_sum | total_sales_qty
  const [search, setSearch] = useState('');

  const { token, showToast } = useStateContext();

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return params.toString();
  }, [startDate, endDate]);

  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      const response = await loadList('/admin-api/stores', '', token);
      const queryString = buildQueryString();
      const storesData = await Promise.all(
        response.rows.map(async (store) => {
          const jsonData = await storeService.getProductStats(store.id, queryString, token);
          return { ...store, productStats: jsonData.response };
        })
      );
      setStores(storesData);
      if (storesData.length > 0 && selectedStores.length === 0) {
        setSelectedStores(storesData.slice(0, Math.min(3, storesData.length)).map(s => s.id));
      }
    } catch (error) {
      showToast({ title: 'Ошибка', content: error.message || 'Ошибка загрузки магазинов', cssClass: 'e-toast-danger', icon: 'e-error toast-icons' });
    } finally {
      setLoading(false);
    }
  }, [buildQueryString, token, showToast, selectedStores.length]);

  useEffect(() => { fetchStores(); }, []);

  const toggleStore = (storeId) => {
    setSelectedStores(prev => prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]);
  };

  // Aggregate products across selected stores by product__id
  const aggregatedProducts = useMemo(() => {
    const map = new Map();
    stores
      .filter(s => selectedStores.includes(s.id))
      .forEach(store => {
        const data = store.productStats?.data || [];
        data.forEach(p => {
          const key = p.product__id;
          const existing = map.get(key) || {
            product__id: p.product__id,
            product__name: p.product__name,
            opening_qty: 0,
            opening_sum: 0,
            opening_purchase: 0,
            closing_qty: 0,
            closing_sum: 0,
            closing_purchase: 0,
            total_sales_count: 0,
            total_sales_qty: 0,
            total_sales_sum: 0,
            total_sales_purchase: 0,
            total_sales_profit: 0,
          };
          const merged = {
            ...existing,
            opening_qty: existing.opening_qty + (p.opening_qty || 0),
            opening_sum: existing.opening_sum + (p.opening_sum || 0),
            opening_purchase: existing.opening_purchase + (p.opening_purchase || 0),
            closing_qty: existing.closing_qty + (p.closing_qty || 0),
            closing_sum: existing.closing_sum + (p.closing_sum || 0),
            closing_purchase: existing.closing_purchase + (p.closing_purchase || 0),
            total_sales_count: existing.total_sales_count + (p.total_sales_count || 0),
            total_sales_qty: existing.total_sales_qty + (p.total_sales_qty || 0),
            total_sales_sum: existing.total_sales_sum + (p.total_sales_sum || 0),
            total_sales_purchase: existing.total_sales_purchase + (p.total_sales_purchase || 0),
            total_sales_profit: existing.total_sales_profit + (p.total_sales_profit || 0),
          };
          map.set(key, merged);
        });
      });

    let rows = Array.from(map.values());
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r => String(r.product__name || '').toLowerCase().includes(q));
    }

    rows.sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
    return rows;
  }, [stores, selectedStores, sortBy, search]);

  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, `${filename}_${startDate}_${endDate}.xlsx`);
  };

  const exportToHTML = (data, filename) => {
    const headers = [
      'Товар', 'Продажи (шт)', 'Продажи (сумма)', 'Прибыль',
      'Себестоимость продаж', 'Остаток (шт)', 'Остаток (сумма)'
    ];
    const rows = data.map(r => [
      r.product__name,
      r.total_sales_qty,
      r.total_sales_sum,
      r.total_sales_profit,
      r.total_sales_purchase,
      r.closing_qty,
      r.closing_sum,
    ]);

    const tableHtml = `<!doctype html><html><head><meta charset="utf-8"><title>${filename}</title>
      <style>table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:right}th:first-child,td:first-child{text-align:left}th{background:#f4f4f5}</style>
      </head><body>
      <h2>${filename} (${startDate} → ${endDate})</h2>
      <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${typeof c === 'number' ? formatMoney(c) : c}</td>`).join('')}</tr>`).join('')}</tbody>
      </table></body></html>`;

    const blob = new Blob([tableHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${startDate}_${endDate}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totalSummary = useMemo(() => {
    return aggregatedProducts.reduce((acc, r) => ({
      qty: acc.qty + (r.total_sales_qty || 0),
      sales: acc.sales + (r.total_sales_sum || 0),
      profit: acc.profit + (r.total_sales_profit || 0),
      stock: acc.stock + (r.closing_sum || 0),
    }), { qty: 0, sales: 0, profit: 0, stock: 0 });
  }, [aggregatedProducts]);

  return (
    <div className="p-4">
      <Header title="Статистика по товарам" />

      <div className="bg-white p-4 rounded-md shadow mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-sm text-gray-600">Начало</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-gray-600">Конец</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-gray-600">Сортировка</label>
            <select className="w-full border rounded px-3 py-2" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="total_sales_sum">Сумма продаж</option>
              <option value="total_sales_profit">Прибыль</option>
              <option value="closing_sum">Остаток (сумма)</option>
              <option value="total_sales_qty">Кол-во продаж</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Поиск товара</label>
            <input type="text" className="w-full border rounded px-3 py-2" placeholder="Название товара" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-2 rounded bg-gray-100 border" onClick={fetchStores} disabled={loading}>{loading ? 'Загрузка...' : 'Обновить'}</button>
            <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={() => exportToExcel(aggregatedProducts, 'products_stats')}>Экспорт Excel</button>
            <button className="px-3 py-2 rounded bg-indigo-600 text-white" onClick={() => exportToHTML(aggregatedProducts, 'products_stats')}>Скачать HTML</button>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm text-gray-600 mb-2">Магазины</div>
          <div className="flex flex-wrap gap-2">
            {stores.map(store => (
              <button
                key={store.id}
                className={`px-3 py-1 rounded border ${selectedStores.includes(store.id) ? 'bg-green-100 border-green-400' : 'bg-gray-100'}`}
                onClick={() => toggleStore(store.id)}
              >
                {store.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-md shadow">
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-gray-600">Всего позиций: {aggregatedProducts.length}</div>
          <div className="text-sm text-gray-600">
            Итого — Продажи: {formatMoney(totalSummary.sales)} | Прибыль: {formatMoney(totalSummary.profit)} | Остаток: {formatMoney(totalSummary.stock)}
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="text-left p-2">Товар</th>
                <th className="text-right p-2">Продажи (шт)</th>
                <th className="text-right p-2">Продажи (сумма)</th>
                <th className="text-right p-2">Прибыль</th>
                <th className="text-right p-2">Себест. продаж</th>
                <th className="text-right p-2">Остаток (шт)</th>
                <th className="text-right p-2">Остаток (сумма)</th>
              </tr>
            </thead>
            <tbody>
              {aggregatedProducts.map(row => (
                <tr key={row.product__id} className="border-t">
                  <td className="p-2 text-left">{row.product__name}</td>
                  <td className="p-2 text-right">{row.total_sales_qty}</td>
                  <td className="p-2 text-right">{formatMoney(row.total_sales_sum)}</td>
                  <td className="p-2 text-right">{formatMoney(row.total_sales_profit)}</td>
                  <td className="p-2 text-right">{formatMoney(row.total_sales_purchase)}</td>
                  <td className="p-2 text-right">{row.closing_qty}</td>
                  <td className="p-2 text-right">{formatMoney(row.closing_sum)}</td>
                </tr>
              ))}
              {aggregatedProducts.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-gray-500">Нет данных для отображения</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductStats;
